import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Tile, Tag, InlineNotification, Loading } from '@carbon/react';
import { ArrowLeft, Settings, Wifi } from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';
import { useSubscription } from '../hooks/useSubscription';
import SubscriptionGate from '../components/SubscriptionGate';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../services/supabaseClient';
import { getGatewayProductFamily, getGatewayModelDisplay, getGatewayProductTagType } from '../utils/gatewayProductLabel';
import { getGatewayAdminCapabilities } from '../utils/gatewayAdminCapabilities';
import GatewayAdminView from '../features/gateway-admin/components/GatewayAdminView';
import './GatewayConfigPage.scss';

/**
 * Per-gateway settings page — mirrors native GatewayAdmin / LilyGoGatewayAdmin.
 * Opened from Gateway Detail via the settings icon (not a modal).
 */
const GatewaySettingsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuth();
  const { hasActiveSubscription } = useSubscription();

  const [gateway, setGateway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const bg = isDark ? '#161616' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';
  const tileBg = isDark ? '#262626' : '#ffffff';

  useEffect(() => {
    if (!id || !user?.id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError('');

    supabase
      .from('gateways')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .maybeSingle()
      .then(({ data, error: fetchError }) => {
        if (cancelled) return;
        if (fetchError) {
          setError(fetchError.message || 'Failed to load gateway');
          setGateway(null);
        } else if (!data) {
          setError('Gateway not found');
          setGateway(null);
        } else {
          setGateway(data);
        }
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, user?.id]);

  const label = useMemo(() => getGatewayProductFamily(gateway), [gateway]);
  const modelLabel = useMemo(() => getGatewayModelDisplay(gateway), [gateway]);
  const caps = useMemo(() => getGatewayAdminCapabilities(gateway), [gateway]);
  const isEsp = caps.isEsp;

  if (loading) {
    return (
      <div style={{ background: bg, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading description="Loading gateway settings…" />
      </div>
    );
  }

  return (
    <SubscriptionGate
      feature="gateway settings"
      disabled={!hasActiveSubscription}
      showChildrenGreyed
      customMessage="You need an active subscription to configure gateways and send commands."
    >
      <div
        className="gateway-config-page gateway-settings-page"
        style={{ background: bg, minHeight: '100vh', padding: '16px' }}
      >
        <div style={{ maxWidth: 'min(1280px, 100%)', margin: '0 auto', width: '100%' }}>
          <Button
            kind="ghost"
            renderIcon={ArrowLeft}
            onClick={() => navigate(`/dashboard/gateways/${id}`)}
            style={{ color: textPrimary, marginBottom: '12px' }}
          >
            Back to gateway
          </Button>

          <Tile
            style={{
              background: tileBg,
              border: `1px solid ${borderColor}`,
              padding: '20px',
              marginBottom: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', minWidth: 0 }}>
                <Settings size={28} style={{ color: textPrimary, flexShrink: 0, marginTop: 2 }} />
                <div style={{ minWidth: 0 }}>
                  <h1
                    style={{
                      color: textPrimary,
                      fontSize: '1.35rem',
                      fontWeight: 600,
                      margin: 0,
                      wordBreak: 'break-word',
                    }}
                  >
                    {caps.settingsPageTitle}
                  </h1>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                    <Tag type={getGatewayProductTagType(label)} size="sm">{label}</Tag>
                    {modelLabel && modelLabel !== label ? (
                      <Tag type="outline" size="sm">{modelLabel}</Tag>
                    ) : null}
                    <Tag type="gray" size="sm">{caps.nativeScreenName}</Tag>
                    {gateway?.mac_address && (
                      <span style={{ color: textSecondary, fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {gateway.mac_address}
                      </span>
                    )}
                  </div>
                  <p style={{ color: textSecondary, fontSize: '0.875rem', margin: '8px 0 0', lineHeight: 1.45 }}>
                    {caps.settingsDescription}
                  </p>
                </div>
              </div>

              {isEsp && (
                <Button
                  kind="tertiary"
                  size="sm"
                  renderIcon={Wifi}
                  onClick={() =>
                    navigate('/dashboard/gateways/commission', {
                      state: {
                        gatewayId: gateway.id,
                        macAddress: gateway.mac_address,
                        deviceApiKey: gateway.http_api_key || undefined,
                      },
                    })
                  }
                >
                  Configure network
                </Button>
              )}
            </div>
          </Tile>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              hideCloseButton
              style={{ marginBottom: '16px' }}
            />
          )}

          {!error && gateway && (
            <Tile
              style={{
                background: tileBg,
                border: `1px solid ${borderColor}`,
                padding: '16px',
                overflow: 'hidden',
                minWidth: 0,
              }}
            >
              <GatewayAdminView
                configOnly
                initialGatewayId={gateway.id}
                initialGateway={gateway}
                lockGateway
              />
            </Tile>
          )}
        </div>
      </div>
    </SubscriptionGate>
  );
};

export default GatewaySettingsPage;
